import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, Typography } from '@mui/material';
import { Link as LinkIcon, Search } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useToast } from '../contexts/ToastContext';
import { useOrgFeatureFlags } from '../hooks/useOrgFeatureFlags';
import { apiService } from '../services/api';
import { integrationApiService } from '../services/integrationApi';
import type { ApplyMatchSkip, ApplyMatchesResult, IntegrationConfig, IntegrationSettings, ProductMatchResult, ShopifyScanJob, SyncMode } from '../services/integrationApi';
import { tokens } from '../theme/tokens';
import { SectionCard, StatusChip, statusTone } from './ui';
import ApplyMatchesSkips from './dataIngestion/ApplyMatchesSkips';
import { loadScanMatchesForApply, selectionAddsFor } from './dataIngestion/applyMatchesResult';
import { applyMatchesInSlices } from './dataIngestion/applyMatchesRun';
import UnmatchedListingPlaceholders from './placeholders/UnmatchedListingPlaceholders';

const T = 'integrations.catalogScan';
/** Products added to the sync selection per request, so a long list stays under the body limit. */
const ADD_BATCH = 500;
/** How often the scan job is asked how far it got while it is queued or running. */
const SCAN_POLL_MS = 2500;
/** Consecutive failed status reads, while a job is live, before the panel says it cannot reach it. */
const STATUS_ERRORS_SHOWN = 3;

interface ShopifyCatalogScanProps {
  integration: IntegrationConfig;
  /** Sync mode the matches are applied under. Defaults to the integration's saved mode. */
  syncMode?: SyncMode;
  /** Unsaved settings (setup walkthrough) the matches are applied under instead of the saved ones. */
  settings?: IntegrationSettings;
  /** Tighter chrome for the setup walkthrough. */
  compact?: boolean;
}

/**
 * The one place a store scans its Shopify catalog. The scan is read-only: it
 * finds listings that match Motzu products by SKU or barcode so a sync updates
 * them instead of creating a second one, lets the store link the matches it
 * wants, and lists the listings Motzu does not know (pricing-only placeholders,
 * behind `placeholder_link_only`). Used by the setup walkthrough and the
 * integration's Mapping tab.
 */
const ShopifyCatalogScan: React.FC<ShopifyCatalogScanProps> = ({ integration, syncMode, settings, compact = false }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { isEnabled } = useOrgFeatureFlags();
  const perIntegrationSelection = isEnabled('per_integration_sync_selection');
  const [result, setResult] = useState<ProductMatchResult | null>(integration.settings.lastMatchResult ?? null);
  // Every match is chosen until the store clears it; only the preview's rows can be cleared.
  const [cleared, setCleared] = useState<Set<string>>(() => new Set());
  const [starting, setStarting] = useState(false);
  // Set once this panel has seen the scan queued or running (or queued it itself), so a result it waited for refreshes the integration queries.
  const following = useRef(false);
  // Identity (run id, else finish time) of the finished scan this panel last adopted.
  const shownRun = useRef<string | null>(null);
  const [applying, setApplying] = useState(false);
  // How far the click has got. The button reads this so a long link is not a frozen control.
  const [progress, setProgress] = useState<{ phase: 'prepare' | 'add' | 'link'; done: number; total: number } | null>(null);
  // Matches the last Apply left unlinked, and the ones it linked but could not update in Shopify.
  const [applySkips, setApplySkips] = useState<ApplyMatchesResult['skips']>([]);
  const [updateFailures, setUpdateFailures] = useState<ApplyMatchSkip[]>([]);
  // canonicalProductId → linked (mapping written) or unlinked (a reason is in applySkips).
  const [outcomes, setOutcomes] = useState<Record<string, 'linked' | 'unlinked'>>({});
  const [matchNames, setMatchNames] = useState<Record<string, string>>({});
  const shown = result?.matched ?? [];
  const matchedTotal = Math.max(result?.stats.matchedCount ?? 0, shown.length);
  const previewOnly = shown.length < matchedTotal;
  const outsideCount = result?.stats.matchedOutsideSelection ?? shown.filter((m) => m.inSelection === false).length;
  const clearedOutside = shown.filter((m) => m.inSelection === false && cleared.has(m.canonicalProductId)).length;
  // A gated match is never added to the selection, so it is not counted as one the button adds.
  const gatedOutside = shown.filter((m) => m.inSelection === false && m.catalogGate && !cleared.has(m.canonicalProductId)).length;
  const selectedOutside = Math.max(0, outsideCount - clearedOutside - gatedOutside);
  const linkedOutcomeCount = Object.values(outcomes).filter((status) => status === 'linked').length;
  // A linked row the store then clears is in both sets; count it once.
  const clearedLinked = Array.from(cleared).filter((id) => outcomes[id] === 'linked').length;
  const selectedCount = Math.max(0, matchedTotal - cleared.size - linkedOutcomeCount + clearedLinked);
  const unmatchedMotzuCount = result?.stats.unmatchedMotzuCount ?? result?.unmatchedMotzu.length ?? 0;
  const mode: SyncMode = syncMode ?? integration.settings.syncMode ?? 'link_only';

  // A different integration starts from its own last result, never the previous one's.
  useEffect(() => {
    const last = integration.settings.lastMatchResult ?? null;
    setResult(last);
    setCleared(new Set());
    setOutcomes({});
    setApplySkips([]);
    setUpdateFailures([]);
    setMatchNames({});
    following.current = false;
    shownRun.current = null;
    // Reseed on the integration id only: a refetch must not wipe a fresh scan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration.id]);

  // The scan is a background job: the POST only queues it, and this poll reports progress until it ends.
  const statusKey = ['shopify-scan-status', integration.id];
  const statusQuery = useQuery({
    queryKey: statusKey,
    queryFn: () => integrationApiService.getScanStatus(integration.id),
    refetchInterval: (query) => {
      const state = query.state.data?.status;
      return state === 'queued' || state === 'running' ? SCAN_POLL_MS : false;
    },
    // A transient failure while polling must not end the scan; the next tick asks again.
    retry: 1,
  });
  const job = statusQuery.data;
  const jobStatus = job?.status;
  const running = jobStatus === 'queued' || jobStatus === 'running';
  const scanning = starting || running;
  // Reads that keep failing while a job is live: shown once it is clear the status is not coming through.
  const statusUnreachable = running && statusQuery.errorUpdateCount >= STATUS_ERRORS_SHOWN;

  useEffect(() => {
    if (running) {
      following.current = true;
      return;
    }
    if (starting) return;
    if (jobStatus === 'done') {
      const observed = following.current;
      following.current = false;
      // Reconcile by run identity, not by having watched it run: a panel that mounts after the
      // scan finished (tab change, long absence) must show the fresh result too.
      const identity = job?.runId ?? job?.finishedAt ?? null;
      if (!job?.result || !job.result.matched) {
        if (observed) showToast(t(`${T}.badResponse`), 'error');
        return;
      }
      if (identity && identity === shownRun.current) return;
      shownRun.current = identity;
      // The same content already on screen (remount with current props) needs no reload.
      if (result && JSON.stringify(result) === JSON.stringify(job.result)) return;
      setResult(job.result);
      setCleared(new Set());
      setOutcomes({});
      setApplySkips([]);
      setUpdateFailures([]);
      if (observed) {
        queryClient.invalidateQueries({ queryKey: ['integration', integration.id] });
        queryClient.invalidateQueries({ queryKey: ['integrations'] });
      }
    } else if (jobStatus === 'failed') {
      following.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobStatus, running, starting, job?.runId, job?.finishedAt]);

  const scan = async () => {
    setStarting(true);
    following.current = true;
    try {
      await integrationApiService.scanProducts(integration.id);
    } catch (error) {
      following.current = false;
      showToast(error instanceof Error ? error.message : t(`${T}.scanError`), 'error');
      setStarting(false);
      return;
    }
    // The job is queued. Record that first, so polling is live whatever the read below does.
    following.current = true;
    // A read already in flight predates the POST and could land after this and say "none".
    await queryClient.cancelQueries({ queryKey: statusKey });
    queryClient.setQueryData<ShopifyScanJob>(statusKey, (prev) => ({
      status: 'queued',
      runId: null,
      progress: { pagesRead: 0, productsSeen: 0 },
      error: null,
      requestedAt: new Date().toISOString(),
      startedAt: null,
      finishedAt: null,
      result: null,
      ...(prev && (prev.status === 'queued' || prev.status === 'running') ? prev : {}),
    }));
    try {
      // Ask straight away: an inline run is already done, and a queued one shows its first progress.
      const first = await integrationApiService.getScanStatus(integration.id);
      // A read that races the worker's claim can still say "none"; the queued state stands then.
      if (first.status !== 'none') queryClient.setQueryData(statusKey, first);
    } catch {
      // Transient: the job is queued and the interval keeps asking.
    } finally {
      setStarting(false);
    }
  };

  /** The scan response is a preview; the rest of a long match list (and each row's catalog gate) is paged from the scan report. */
  const loadAllMatches = (): Promise<ProductMatchResult['matched']> =>
    loadScanMatchesForApply(integration.id, shown, matchedTotal, integrationApiService.getScanMatches);

  /** True only when this integration keeps its own list; an inheriting one must not be given one by an add. */
  const keepsOwnSelection = async (): Promise<boolean> => {
    if (!perIntegrationSelection) return false;
    const rows = await queryClient.fetchQuery({
      queryKey: ['store-sync-selections'],
      queryFn: () => apiService.getStoreSyncSelections(),
      staleTime: 0,
    });
    return rows.some((row) => row.integrationId === integration.id && !row.inheritsFromOrganization);
  };

  type ScanMatch = ProductMatchResult['matched'][number];

  /** Fold one finished slice into the rows already on screen. `part` is the total for this click. */
  const recordSlice = (batch: Array<{ canonicalProductId: string }>, part: ApplyMatchesResult, done: number) => {
    const failed = new Set(part.skips.map((skip) => skip.canonicalProductId));
    const decided = new Set(batch.slice(0, done).map((row) => row.canonicalProductId));
    setOutcomes((prev) => {
      const next = { ...prev };
      for (const id of Array.from(decided)) next[id] = failed.has(id) ? 'unlinked' : 'linked';
      return next;
    });
    setApplySkips((prev) => [...prev.filter((skip) => !decided.has(skip.canonicalProductId)), ...part.skips]);
    setUpdateFailures((prev) => [
      ...prev.filter((skip) => !decided.has(skip.canonicalProductId)),
      ...(part.updateFailures ?? []),
    ]);
  };

  const linkChosen = async (chosen: ScanMatch[]) => {
    setMatchNames((prev) => {
      const next = { ...prev };
      for (const row of chosen) next[row.canonicalProductId] = row.canonicalName;
      return next;
    });
    const sent = new Set(chosen.map((row) => row.canonicalProductId));
    setApplySkips((prev) => prev.filter((skip) => !sent.has(skip.canonicalProductId)));
    setUpdateFailures((prev) => prev.filter((skip) => !sent.has(skip.canonicalProductId)));
    // A product of the store's brands joins the sync selection first, so the link and
    // every later sync treat it like any product the store picked itself. One a catalog
    // gate excludes is not added: Apply skips it and lists the setting that controls it.
    const toAdd = selectionAddsFor(chosen);
    const added = new Set(toAdd);
    if (toAdd.length > 0) {
      setProgress({ phase: 'add', done: 0, total: toAdd.length });
      const target = (await keepsOwnSelection()) ? integration.id : undefined;
      for (let i = 0; i < toAdd.length; i += ADD_BATCH) {
        await apiService.addStoreProductsToSync(toAdd.slice(i, i + ADD_BATCH), target);
        setProgress({ phase: 'add', done: Math.min(i + ADD_BATCH, toAdd.length), total: toAdd.length });
      }
      queryClient.invalidateQueries({ queryKey: ['store-sync-selection'] });
      queryClient.invalidateQueries({ queryKey: ['store-sync-selections'] });
      queryClient.invalidateQueries({ queryKey: ['setup-walkthrough-selection'] });
      setResult((current) => current && ({
        ...current,
        matched: current.matched.map((m) => (added.has(m.canonicalProductId) ? { ...m, inSelection: true } : m)),
        stats: { ...current.stats, matchedOutsideSelection: Math.max(0, (current.stats.matchedOutsideSelection ?? 0) - added.size) },
      }));
    }
    const matches = chosen.map((m) => ({
      shopifyProductId: m.shopifyProductId,
      canonicalProductId: m.canonicalProductId,
    }));
    const pending = settings && {
      syncImages: settings.syncImages,
      syncPricing: settings.syncPricing,
      syncInventory: settings.syncInventory,
      addMotzuTag: settings.addMotzuTag,
    };
    setProgress({ phase: 'link', done: 0, total: matches.length });
    const applied = await applyMatchesInSlices({
      matches,
      apply: (slice) => integrationApiService.applyMatches(integration.id, slice, mode, pending),
      onProgress: (p) => setProgress({ phase: 'link', done: p.done, total: p.total }),
      onSlice: (part, done) => recordSlice(matches, part, done),
      // A dropped enrich or override slice is not sent again: the first call may still be writing it.
      retryDropped: mode === 'link_only',
    });
    // A match a catalog gate skipped is named in the toast and listed below with the setting that controls it.
    const notLinked = applied.skipped > 0 || (applied.updateFailures?.length ?? 0) > 0;
    showToast(
      t(notLinked ? `${T}.appliedWithSkipped` : `${T}.applied`, { ...applied, skipped: applied.skipped }),
      notLinked ? 'warning' : 'success',
    );
    queryClient.invalidateQueries({ queryKey: ['integration', integration.id] });
    queryClient.invalidateQueries({ queryKey: ['shopify-listing-holds', integration.id] });
  };

  const apply = async () => {
    if (!result) return;
    setApplying(true);
    setProgress({ phase: 'prepare', done: 0, total: selectedCount });
    try {
      const chosen = (await loadAllMatches()).filter(
        (m) => !cleared.has(m.canonicalProductId) && outcomes[m.canonicalProductId] !== 'linked',
      );
      if (chosen.length === 0) return;
      await linkChosen(chosen);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t(`${T}.applyError`), 'error');
    } finally {
      setApplying(false);
      setProgress(null);
    }
  };

  const retrySkips = async (skips: ApplyMatchSkip[]) => {
    if (!result || skips.length === 0) return;
    setApplying(true);
    setProgress({ phase: 'prepare', done: 0, total: skips.length });
    try {
      const byId = new Map((await loadAllMatches()).map((row) => [row.canonicalProductId, row]));
      const chosen = skips.flatMap((skip) => {
        const row = byId.get(skip.canonicalProductId);
        return row ? [row] : [];
      });
      if (chosen.length === 0) return;
      await linkChosen(chosen);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t(`${T}.applyError`), 'error');
    } finally {
      setApplying(false);
      setProgress(null);
    }
  };

  const progressLabel = progress
    ? progress.phase === 'add'
      ? t(`${T}.addingSelection`, { done: progress.done, total: progress.total })
      : progress.phase === 'link'
        ? t(`${T}.linking`, { done: progress.done, total: progress.total })
        : t(`${T}.preparing`)
    : null;

  const scanButton = (
    <Button
      size="small"
      variant={result ? 'outlined' : 'contained'}
      startIcon={scanning ? <CircularProgress size={14} color="inherit" /> : <Search />}
      onClick={scan}
      disabled={scanning}
    >
      {scanning ? t(`${T}.scanning`) : result ? t(`${T}.rescan`) : t(`${T}.scan`)}
    </Button>
  );

  return (
    <SectionCard
      title={compact ? undefined : t(`${T}.title`)}
      subtitle={compact ? undefined : t(`${T}.subtitle`)}
      actions={compact ? undefined : scanButton}
      padded
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {compact && <Box>{scanButton}</Box>}
        {scanning && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} role="status">
            <StatusChip
              label={t(jobStatus === 'queued' || !job || starting ? `${T}.queued` : `${T}.running`)}
              tone={statusTone(jobStatus === 'queued' || starting ? 'queued' : 'running')}
            />
            <Typography variant="caption" color="text.secondary">
              {job && jobStatus === 'running' && job.progress.pagesRead > 0
                ? t(`${T}.progress`, {
                    pages: job.progress.pagesRead.toLocaleString(),
                    products: job.progress.productsSeen.toLocaleString(),
                  })
                : t(`${T}.starting`)}
            </Typography>
          </Box>
        )}
        {statusUnreachable && (
          <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
            {t(`${T}.statusUnreachable`)}
          </Alert>
        )}
        {!scanning && jobStatus === 'failed' && (
          <Alert
            severity="error"
            sx={{ borderRadius: 1.5 }}
            action={
              <Button color="inherit" size="small" onClick={scan}>
                {t(`${T}.retry`)}
              </Button>
            }
          >
            {job?.error || t(`${T}.scanError`)}
          </Alert>
        )}
        {!result && !scanning && (
          <Typography variant="body2" color="text.secondary">
            {t(`${T}.idle`)}
          </Typography>
        )}

        {result && (
          <>
            <Alert severity={result.stats.matchedCount > 0 ? 'success' : 'info'} sx={{ borderRadius: 1.5 }}>
              {outsideCount > 0
                ? t(`${T}.summaryCatalog`, {
                    matched: result.stats.matchedCount,
                    outside: outsideCount,
                    scanned: result.stats.totalShopify,
                  })
                : t(`${T}.summary`, {
                    matched: result.stats.matchedCount,
                    selected: result.stats.totalMotzu,
                    rate: result.stats.matchRate,
                    scanned: result.stats.totalShopify,
                  })}
            </Alert>
            {result.stats.catalogTruncated && <Alert severity="warning">{t(`${T}.truncated`)}</Alert>}

            {shown.length > 0 && (
              <>
                <Box sx={{ border: `1px solid ${tokens.line.default}`, borderRadius: 1.5, maxHeight: 240, overflow: 'auto' }}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      position: 'sticky',
                      top: 0,
                      display: 'flex',
                      justifyContent: 'space-between',
                      bgcolor: tokens.surface.tint,
                      borderBottom: `1px solid ${tokens.line.default}`,
                    }}
                  >
                    <Typography variant="overline">{t(`${T}.matchedTitle`, { count: matchedTotal })}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(`${T}.selectedCount`, { count: selectedCount })}
                    </Typography>
                  </Box>
                  {shown.map((m, idx, arr) => (
                    <Box
                      key={m.canonicalProductId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.5,
                        ...(idx < arr.length - 1 ? { borderBottom: `1px solid ${tokens.line.hairline}` } : {}),
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={!cleared.has(m.canonicalProductId)}
                        onChange={(e) => {
                          const next = new Set(cleared);
                          if (e.target.checked) next.delete(m.canonicalProductId);
                          else next.add(m.canonicalProductId);
                          setCleared(next);
                        }}
                        sx={{ p: 0.25 }}
                      />
                      <Typography variant="caption" sx={{ flex: 1, fontWeight: 500 }}>
                        {m.canonicalName}
                      </Typography>
                      {outcomes[m.canonicalProductId] === 'linked' && (
                        <StatusChip label={t(`${T}.linkedChip`)} tone="positive" />
                      )}
                      {outcomes[m.canonicalProductId] === 'unlinked' && (
                        <StatusChip label={t(`${T}.rowUnlinked`)} tone="caution" />
                      )}
                      {m.inSelection === false && m.catalogGate ? (
                        <StatusChip
                          label={t(`integrations.lightspeedCatalogMatch.gate.${m.catalogGate.code}`, {
                            defaultValue: t('integrations.lightspeedCatalogMatch.gate.other'),
                          })}
                          tone="caution"
                        />
                      ) : (
                        m.inSelection === false && (
                          <Chip label={t(`${T}.notSelected`)} size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                        )
                      )}
                      <Chip label={m.matchedBy} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {m.shopifyTitle}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {previewOnly && (
                  <Typography variant="caption" color="text.secondary">
                    {t(`${T}.previewNote`, { shown: shown.length, total: matchedTotal })}
                  </Typography>
                )}
                <Box>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={applying || selectedCount === 0}
                    aria-busy={applying || undefined}
                    startIcon={applying ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}
                    onClick={apply}
                    sx={applying ? { '&.Mui-disabled': { opacity: 1, color: 'primary.contrastText', backgroundColor: 'primary.main' } } : undefined}
                  >
                    {progressLabel ?? t(selectedOutside > 0 ? `${T}.applyAdd` : `${T}.apply`, { count: selectedCount, outside: selectedOutside, mode: t(`integrations.setupWalkthrough.summary.modes.${mode === 'link_only' ? 'link_only_shopify' : mode}`) })}
                  </Button>
                </Box>
                <ApplyMatchesSkips
                  integrationId={integration.id}
                  skips={applySkips}
                  updateFailures={updateFailures}
                  names={matchNames}
                  onRetry={retrySkips}
                  retrying={applying}
                />
              </>
            )}

            {unmatchedMotzuCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                {t(`${T}.unmatchedMotzu`, { count: unmatchedMotzuCount })}
              </Typography>
            )}

            <UnmatchedListingPlaceholders integrationId={integration.id} listings={result.unmatchedShopify || []} />
          </>
        )}
      </Box>
    </SectionCard>
  );
};

export default ShopifyCatalogScan;
